<template>
  <div>
    <BaseSeparator />
    <div class="tw:flex tw:justify-end tw:gap-2 tw:p-4">
      <BaseButton
        v-if="mediaReleaseSuggestions"
        color="primary"
        data-testid="add-recipients-button"
        :disabled="!selectedIds.length || updating"
        :label="getLocaleText('ai_suggested_contacts_panel.add_recipients')"
        new-design
        @click="handleAddRecipients"
      />
      <template v-else>
        <OutreachContactActions
          button-color="secondary"
          data-testid="contact-actions"
          :disable="disableContactActions"
          :label="
            getLocaleText('ai_suggested_contacts_panel.add_to_other_list')
          "
          :selected-ids="selectedIds"
          suggested-contacts
          @action="handleAction"
          @add="handleAddToListsClick"
        />
        <BaseButton
          color="primary"
          data-testid="add-contacts-button"
          :disabled="disabledAddToList"
          icon="plus"
          :label="getLocaleText('ai_suggested_contacts_panel.add_contacts')"
          new-design
          new-icon
          @click="handleAddContacts"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed } from "vue";
import { useRoute } from "vue-router";

import { track } from "shared/boot/analytics";
import { getLocaleText } from "shared/boot/i18n";
import { BaseButton, BaseSeparator } from "shared/components/base";
import OutreachContactActions, {
  Action,
} from "shared/components/Outreach/OutreachContactActions.vue";
import { type ContactList, type MediaRelease } from "shared/resources";
import { MediaReleaseType } from "shared/resources/MediaRelease";
import useAIStore from "shared/stores/ai";
import { type Nullable } from "shared/types";

const route = useRoute();

interface AISuggestedContactsFooterProps {
  assistantPanel?: boolean;
  contactList?: ContactList;
  mediaRelease?: MediaRelease;
  selectedIds: string[];
  updating?: boolean;
}

const {
  assistantPanel,
  selectedIds = [],
  updating,
  mediaRelease,
} = defineProps<AISuggestedContactsFooterProps>();

const emit = defineEmits(["close", "add"]);

const aiStore = useAIStore();

const { aiSuggestedContactsFilter } = storeToRefs(aiStore);

const mediaReleaseSuggestions = computed<boolean>(
  () => "suggestionsForMediaReleaseId" in aiSuggestedContactsFilter.value
);

const mediaReleaseType = computed<Nullable<string>>(() => {
  if (mediaRelease && mediaRelease.mediaReleaseType === MediaReleaseType.Pitch)
    return "Pitch";

  if (mediaReleaseSuggestions.value) return "Media Release";

  return null;
});

const disableContactActions = computed<boolean>(
  () => !selectedIds.length || updating
);

const disabledAddToList = computed<boolean>(
  () =>
    disableContactActions.value ||
    route.name !== "outreach-lists-contacts" ||
    Number(route.params.listId) !==
      Number(aiSuggestedContactsFilter.value.suggestionsForContactListId)
);

const trackingPanel = computed<string>(() => {
  const panel = mediaReleaseType.value || "List Suggestions";

  return assistantPanel ? "General Assistant" : panel;
});

function handleAddRecipients() {
  track(`Clicked Add to Recipient List from ${trackingPanel.value}`);
  emit("add");
}

function handleAddContacts() {
  track(`Clicked Add to this list from ${trackingPanel.value}`);
  emit("add");
}

function handleAddToListsClick() {
  track(`Clicked Add to other list from ${trackingPanel.value}`);
}

function handleAction(action: Action) {
  const panel = assistantPanel ? "General Assistant" : trackingPanel.value;

  switch (action) {
    case Action.CREATE_MEDIA_RELEASE:
      track(`Clicked Send Media Release from ${panel}`);
      break;
    case Action.CREATE_PITCH:
      track(`Clicked Send Pitch from ${panel}`);
      break;
    case Action.COPY_EMAILS:
      track(`Clicked Copy Emails from ${panel}`);
      break;
    case Action.EXPORT_CONTACTS:
      track(`Clicked Download CSV from ${panel}`);
      break;
    default:
  }
}
</script>
